import { ChangeEvent, useCallback, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  Plus,
  FileText,
  Calendar,
  DollarSign,
  Building,
  Phone,
  Mail,
  MapPin,
  Clock,
  AlertTriangle,
  Eye,
  Edit,
  Search,
  Filter,
  Download,
  Upload,
  Trash2
} from "lucide-react";
import { format, parseISO, addDays, differenceInDays } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { AddressFields } from "@/components/forms/AddressFields";
import { composeAddressLine, emptyAddressFields, normalizeAddressFields } from "@/lib/address";
import { formatEINInputValue, formatEINForDisplay } from "@/lib/ein";

// =========================
// Types & Constants
// =========================
export type W9Status = "pending" | "requested" | "received" | "expired";
export type ContractType = "fixed-term" | "project-based" | "ongoing" | "temporary";
export type ContractorStatus = "active" | "expiring-soon" | "expired";

export interface Contractor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  role: string;
  department?: string;
  contractType: ContractType;
  startDate: string; // ISO date string
  endDate?: string; // ISO date string or undefined
  hourlyRate: number; // numeric for calculations
  address?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  country?: string;
  taxId?: string;
  w9Status: W9Status;
  notes?: string;
  status: ContractorStatus; // derived but stored for quick filtering (kept in sync)
  addedDate: string; // ISO date string
}

interface NewContractorFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  department: string;
  contractType: ContractType;
  startDate: string;
  endDate: string;
  hourlyRate: string; // keep as string in form
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  county: string;
  country: string;
  taxId: string;
  w9Status: W9Status;
  notes: string;
}

// =========================
// Helper Functions (pure)
// =========================
function deriveStatus(startDate: string, endDate?: string): ContractorStatus {
  if (endDate) {
    try {
      const end = parseISO(endDate);
      const today = new Date();
      const days = differenceInDays(end, today);
      if (days < 0) return "expired";
      if (days <= 30) return "expiring-soon";
    } catch {
      // ignore parse errors -> treat as active
    }
  }
  return "active";
}

function computeDaysUntilExpiry(endDate?: string): number | null {
  if (!endDate) return null;
  try {
    const days = differenceInDays(parseISO(endDate), new Date());
    return days >= 0 ? days : 0;
  } catch {
    return null;
  }
}

function validateForm(data: NewContractorFormState): string | null {
  if (!data.firstName || !data.lastName || !data.email || !data.role) return "Missing required fields";
  if (!data.startDate) return "Start date required";
  if (data.endDate) {
    try {
      if (new Date(data.endDate) < new Date(data.startDate)) return "End date cannot be before start date";
    } catch {
      return "Invalid date format";
    }
  }
  if (data.hourlyRate && Number(data.hourlyRate) < 0) return "Hourly rate cannot be negative";
  return null;
}

function serializeForExport(contractors: Contractor[]): string {
  return JSON.stringify(contractors, null, 2);
}

function parseImportedContractors(text: string): Contractor[] {
  const raw = JSON.parse(text);
  if (!Array.isArray(raw)) throw new Error("File must contain an array");
  return raw.map((r) => {
    const status = deriveStatus(r.startDate, r.endDate);
    return {
      ...r,
      hourlyRate: Number(r.hourlyRate) || 0,
      status,
    } as Contractor;
  });
}

export default function Contractors() {
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  const [editingContractor, setEditingContractor] = useState<Contractor | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | ContractorStatus>("all");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const blankForm: NewContractorFormState = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    department: "",
    contractType: "fixed-term",
    startDate: "",
    endDate: "",
    hourlyRate: "",
    ...emptyAddressFields(),
    taxId: "",
    w9Status: "pending",
    notes: ""
  };

  const form = useForm({ defaultValues: blankForm });

  const formValues = form.watch();

  const handleNewContractorPhoneChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const formattedPhone = formatPhoneInputValue(event.target.value);

    setNewContractor(previous => ({
      ...previous,
      phone: formattedPhone,
    }));
  };

  const queryClient = useQueryClient();

  // Fetch contractors from employees endpoint (employmentType = contractor)
  const { data: contractorRows = [], isLoading, error } = useQuery({
    queryKey: ["/api/employees", { type: "contractor" }],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/employees");
      const json = await res.json();
      return (json as any[]).filter(r => (r.employmentType || "").toLowerCase() === "contractor");
    }
  });

  const contractorsWithDerivedStatus = useMemo<Contractor[]>(() => {
    return contractorRows.map(r => ({
      id: String(r.id),
      firstName: r.firstName,
      lastName: r.lastName,
      email: r.email,
      phone: r.phone || undefined,
      company: r.company || r.position || undefined,
      role: r.position || r.role || "Contractor",
      department: r.department || undefined,
      contractType: (r.contractType || "fixed-term") as ContractType,
      startDate: r.contractStartDate || r.hireDate || format(new Date(), 'yyyy-MM-dd'),
      endDate: r.contractEndDate || undefined,
      hourlyRate: r.hourlyRate ? Number(r.hourlyRate) : 0,
      address: r.address1 || undefined,
      address1: r.address1 || undefined,
      address2: r.address2 || undefined,
      city: r.city || undefined,
      state: r.state || undefined,
      zipCode: r.zipCode || undefined,
      county: r.county || undefined,
      country: r.country || undefined,
      taxId: r.taxId || undefined,
      w9Status: (r.w9Status || 'pending') as W9Status,
      notes: r.contractorNotes || undefined,
      addedDate: r.createdAt ? String(r.createdAt).split('T')[0] : format(new Date(), 'yyyy-MM-dd'),
      status: deriveStatus(r.contractStartDate || r.hireDate, r.contractEndDate)
    }));
  }, [contractorRows]);

  // Filter contractors (search + status)
  const filteredContractors = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return contractorsWithDerivedStatus.filter(c => {
      const matchesSearch = !term ||
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(term) ||
        (c.company || "").toLowerCase().includes(term) ||
        c.role.toLowerCase().includes(term);
      const matchesStatus = filterStatus === "all" || c.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [contractorsWithDerivedStatus, searchTerm, filterStatus]);

  // Metrics (always derived so status stays consistent)
  const activeContractors = contractorsWithDerivedStatus.filter(c => c.status === "active").length;
  const pendingContracts = contractorsWithDerivedStatus.filter(c => c.w9Status === "pending").length;
  const expiringSoon = contractorsWithDerivedStatus.filter(c => c.status === "expiring-soon").length;

  const invalidateEmployees = () => queryClient.invalidateQueries({ queryKey: ["/api/employees", { type: "contractor" }] });
  const createMutation = useMutation({
    mutationFn: async (payload: any) => apiRequest("POST", "/api/employees", payload),
    onSuccess: invalidateEmployees
  });
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => apiRequest("PATCH", `/api/employees/${id}`, data),
    onSuccess: invalidateEmployees
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/employees/${id}`),
    onSuccess: invalidateEmployees
  });

  const handleAddContractor = () => {
    const error = validateForm(newContractor);
    if (error) {
      alert(error);
      return;
    }
    const addressLine = composeAddressLine(newContractor as any);
    const payload = {
      firstName: newContractor.firstName.trim(),
      lastName: newContractor.lastName.trim(),
      email: newContractor.email.trim(),
      phone: newContractor.phone.trim() || undefined,
      position: newContractor.role.trim(),
      department: newContractor.department || undefined,
      employmentType: 'contractor',
      contractType: newContractor.contractType,
      contractStartDate: newContractor.startDate,
      contractEndDate: newContractor.endDate || null,
  hourlyRate: newContractor.hourlyRate ? Number(newContractor.hourlyRate) : null,
      taxId: newContractor.taxId || null,
      w9Status: newContractor.w9Status,
      contractorNotes: newContractor.notes || null,
      // standardized address mapping
      address: addressLine || null,
      address1: newContractor.address1 || null,
      address2: newContractor.address2 || null,
      city: newContractor.city || null,
      state: newContractor.state || null,
      zipCode: newContractor.zipCode || null,
      county: newContractor.county || null,
      country: newContractor.country || null,
      status: 'active'
    };
    createMutation.mutate(payload);
    setShowAddContractor(false);
    resetForm();
  };

  const handleEditContractor = (contractor: Contractor) => {
    setEditingContractor(contractor);
    const addressDefaults = normalizeAddressFields({
      address1: contractor.address1,
      address2: contractor.address2,
      city: contractor.city,
      state: contractor.state,
      zipCode: contractor.zipCode,
      county: contractor.county,
      country: contractor.country,
    });
    setNewContractor({
      firstName: contractor.firstName,
      lastName: contractor.lastName,
      email: contractor.email,
  phone: contractor.phone || "",
  company: contractor.company || "",
      role: contractor.role,
  department: contractor.department || "",
      contractType: contractor.contractType,
      startDate: contractor.startDate,
  endDate: contractor.endDate || "",
      hourlyRate: String(contractor.hourlyRate),
  ...addressDefaults,
  taxId: contractor.taxId || "",
  w9Status: contractor.w9Status,
  notes: contractor.notes || ""
    });
    setShowAddContractor(true);
  };

  const handleUpdateContractor = () => {
    if (!editingContractor) return;
    const error = validateForm(newContractor);
    if (error) {
      alert(error);
      return;
    }
    const addressLine = composeAddressLine(newContractor as any);
    updateMutation.mutate({
      id: editingContractor.id,
      data: {
        firstName: newContractor.firstName.trim(),
        lastName: newContractor.lastName.trim(),
        email: newContractor.email.trim(),
        phone: newContractor.phone.trim() || undefined,
        position: newContractor.role.trim(),
        department: newContractor.department || undefined,
        contractType: newContractor.contractType,
        contractStartDate: newContractor.startDate,
        contractEndDate: newContractor.endDate || null,
  hourlyRate: newContractor.hourlyRate ? Number(newContractor.hourlyRate) : null,
        taxId: newContractor.taxId || null,
        w9Status: newContractor.w9Status,
        contractorNotes: newContractor.notes || null,
        address: addressLine || null,
        address1: newContractor.address1 || null,
        address2: newContractor.address2 || null,
        city: newContractor.city || null,
        state: newContractor.state || null,
        zipCode: newContractor.zipCode || null,
        county: newContractor.county || null,
        country: newContractor.country || null,
      }
    });
    setEditingContractor(null);
    setShowAddContractor(false);
    resetForm();
  };

  const resetForm = () => setNewContractor(blankForm);

  const getStatusBadge = (contractor: Contractor) => {
    if (contractor.status === "expiring-soon") return <Badge variant="destructive">Expiring Soon</Badge>;
    if (contractor.status === "expired") return <Badge variant="secondary">Expired</Badge>;
    if (contractor.w9Status === "pending") return <Badge variant="outline">W-9 Pending</Badge>;
    return <Badge variant="default">Active</Badge>;
  };

  const handleDelete = useCallback((id: string) => {
    if (confirm("Delete this contractor?")) {
      deleteMutation.mutate(id);
      if (selectedContractor?.id === id) setSelectedContractor(null);
    }
  }, [deleteMutation, selectedContractor]);

  const triggerImport = () => fileInputRef.current?.click();

  // --- CSV Import/Export Helpers ---
  const csvHeaders = [
    'firstName','lastName','email','role','company','department','contractType','startDate','endDate','hourlyRate','w9Status','phone','address1','address2','city','state','zipCode','county','country','taxId','notes'
  ];

  function escapeCsv(val: any): string {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g,'""') + '"';
    return s;
  }

  function buildCsv(rows: Contractor[]): string {
    const lines = [csvHeaders.join(',')];
    for (const r of rows) {
      const line = [
        r.firstName,
        r.lastName,
        r.email,
        r.role,
        r.company || '',
        r.department || '',
        r.contractType,
        r.startDate,
        r.endDate || '',
        r.hourlyRate,
        r.w9Status,
        r.phone || '',
        r.address1 || '',
        r.address2 || '',
        r.city || '',
        r.state || '',
        r.zipCode || '',
        r.county || '',
        r.country || '',
        formatEINForDisplay(r.taxId) || '',
        r.notes || ''
      ].map(escapeCsv).join(',');
      lines.push(line);
    }
    return lines.join('\n');
  }

  function parseCsv(text: string): Record<string,string>[] {
    const rows: Record<string,string>[] = [];
    const lines = text.split(/\r?\n/).filter(l=>l.trim().length>0);
    if (lines.length === 0) return rows;
    const headerLine = lines[0];
    const headers = parseCsvLine(headerLine).map(h=>h.trim());
    for (let i=1;i<lines.length;i++) {
      const parts = parseCsvLine(lines[i]);
      if (parts.every(p=>p.trim()==='')) continue; // skip empty
      const rec: Record<string,string> = {};
      headers.forEach((h,idx)=>{ rec[h] = parts[idx] || ''; });
      rows.push(rec);
    }
    return rows;
  }

  function parseCsvLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i=0;i<line.length;i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (line[i+1] === '"') { cur += '"'; i++; } else { inQuotes = false; }
        } else { cur += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { out.push(cur); cur=''; }
        else { cur += ch; }
      }
    }
    out.push(cur);
    return out;
  }

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const text = String(reader.result || '');
        const records = parseCsv(text);
        if (records.length === 0) throw new Error('CSV empty');
        // Validate headers minimally
        const missing = ['firstName','lastName','email','contractType','startDate'].filter(h => !Object.keys(records[0]).includes(h));
        if (missing.length) throw new Error('Missing required columns: ' + missing.join(', '));

        // Create contractors sequentially (could batch optimize later)
        const payloads = records.map(r => ({
          firstName: r.firstName?.trim(),
          lastName: r.lastName?.trim(),
          email: r.email?.trim(),
          phone: r.phone?.trim() || undefined,
          position: (r.role || '').trim() || 'Contractor',
          department: r.department?.trim() || undefined,
          employmentType: 'contractor',
          contractType: (r.contractType || 'fixed-term').trim(),
          contractStartDate: r.startDate?.trim(),
          contractEndDate: r.endDate?.trim() || null,
          hourlyRate: r.hourlyRate ? Number(r.hourlyRate) : null,
          taxId: r.taxId?.trim() || null,
          w9Status: (r.w9Status || 'pending').trim(),
          contractorNotes: r.notes?.trim() || null,
          address1: r.address1?.trim() || null,
          address2: r.address2?.trim() || null,
          city: r.city?.trim() || null,
          state: r.state?.trim() || null,
          zipCode: r.zipCode?.trim() || null,
          county: r.county?.trim() || null,
          country: r.country?.trim() || null,
          status: 'active'
        }));
        // Fire sequentially to avoid flooding
        for (const p of payloads) {
          try { await apiRequest('POST','/api/employees', p); } catch(err) { console.warn('Row create failed', err); }
        }
        await queryClient.invalidateQueries({ queryKey: ["/api/employees", { type: "contractor" }] });
        alert(`Imported ${payloads.length} contractors`);
      } catch (err: any) {
        alert(`Import failed: ${err.message}`);
      } finally {
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleExport = () => {
    const csv = buildCsv(contractorsWithDerivedStatus);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contractors-${format(new Date(), 'yyyyMMdd-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Contractor Management</h1>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="text/csv,.csv"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button variant="outline" size="sm" onClick={triggerImport}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Dialog open={showAddContractor} onOpenChange={setShowAddContractor}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Contractor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingContractor ? 'Edit Contractor' : 'Add New Contractor'}</DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="personal" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="personal">Personal Info</TabsTrigger>
                  <TabsTrigger value="contract">Contract Details</TabsTrigger>
                  <TabsTrigger value="contact">Contact & Address</TabsTrigger>
                  <TabsTrigger value="tax">Tax & Compliance</TabsTrigger>
                </TabsList>

                <TabsContent value="personal" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        value={newContractor.firstName}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, firstName: e.target.value }))}
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        value={newContractor.lastName}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, lastName: e.target.value }))}
                        placeholder="Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company/Agency</Label>
                      <Input
                        id="company"
                        value={newContractor.company}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, company: e.target.value }))}
                        placeholder="Consulting LLC"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role/Position *</Label>
                      <Input
                        id="role"
                        value={newContractor.role}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="Software Developer"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="department">Department</Label>
                      <Select value={newContractor.department} onValueChange={(value) => setNewContractor(prev => ({ ...prev, department: value }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Engineering">Engineering</SelectItem>
                          <SelectItem value="Marketing">Marketing</SelectItem>
                          <SelectItem value="Sales">Sales</SelectItem>
                          <SelectItem value="Operations">Operations</SelectItem>
                          <SelectItem value="Finance">Finance</SelectItem>
                          <SelectItem value="HR">HR</SelectItem>
                          <SelectItem value="Customer Success">Customer Success</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="contract" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="contractType">Contract Type *</Label>
                      <Select value={newContractor.contractType} onValueChange={(value: ContractType) => setNewContractor(prev => ({ ...prev, contractType: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed-term">Fixed Term</SelectItem>
                          <SelectItem value="project-based">Project Based</SelectItem>
                          <SelectItem value="ongoing">Ongoing</SelectItem>
                          <SelectItem value="temporary">Temporary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hourlyRate">Hourly Rate ($) *</Label>
                      <Input
                        id="hourlyRate"
                        type="number"
                        value={newContractor.hourlyRate}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, hourlyRate: e.target.value }))}
                        placeholder="75"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Contract Start Date *</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={newContractor.startDate}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endDate">Contract End Date</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={newContractor.endDate}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={newContractor.notes}
                      onChange={(e) => setNewContractor(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes about the contractor or project..."
                      rows={3}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="contact" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newContractor.email}
                        onChange={(e) => setNewContractor(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={newContractor.phone}
                        onChange={handleNewContractorPhoneChange}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="address1">Address 1</Label>
                        <Input id="address1" value={newContractor.address1} onChange={(e) => setNewContractor(prev => ({ ...prev, address1: e.target.value }))} placeholder="Street address" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address2">Address 2</Label>
                        <Input id="address2" value={newContractor.address2} onChange={(e) => setNewContractor(prev => ({ ...prev, address2: e.target.value }))} placeholder="Apt, Suite, etc." />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input id="city" value={newContractor.city} onChange={(e) => setNewContractor(prev => ({ ...prev, city: e.target.value }))} placeholder="City" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Input id="state" value={newContractor.state} onChange={(e) => setNewContractor(prev => ({ ...prev, state: e.target.value }))} placeholder="State" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="zip">Zip</Label>
                        <Input id="zip" value={newContractor.zipCode} onChange={(e) => setNewContractor(prev => ({ ...prev, zipCode: e.target.value }))} placeholder="Zip" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="county">County</Label>
                        <Input id="county" value={newContractor.county} onChange={(e) => setNewContractor(prev => ({ ...prev, county: e.target.value }))} placeholder="County" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Input id="country" value={newContractor.country} onChange={(e) => setNewContractor(prev => ({ ...prev, country: e.target.value }))} placeholder="Country" />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="tax" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="taxId">Tax ID/EIN</Label>
                      <Input
                        id="taxId"
                        value={newContractor.taxId}
                        onChange={(e) => {
                          const formatted = formatEINInputValue(e.target.value);
                          setNewContractor(prev => ({ ...prev, taxId: formatted }));
                        }}
                        placeholder="12-3456789"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="w9Status">W-9 Status</Label>
                      <Select value={newContractor.w9Status} onValueChange={(value: W9Status) => setNewContractor(prev => ({ ...prev, w9Status: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="requested">Requested</SelectItem>
                          <SelectItem value="received">Received</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Tax Compliance Reminder</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Ensure all contractors complete Form W-9 before payment processing. 
                      1099 forms will be generated automatically for contractors paid over $600 annually.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAddContractor(false);
                    setEditingContractor(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={editingContractor ? handleUpdateContractor : handleAddContractor}
                  disabled={!!validateForm(newContractor)}
                >
                  {editingContractor ? 'Update Contractor' : 'Add Contractor'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Contractors</CardTitle>
            <Users className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{activeContractors}</div>
            <p className="text-xs text-muted-foreground">Currently working</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending W-9s</CardTitle>
            <FileText className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingContracts}</div>
            <p className="text-xs text-muted-foreground">Need documentation</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{expiringSoon}</div>
            <p className="text-xs text-muted-foreground">Within 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Contractors</CardTitle>
            <Building className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{contractorsWithDerivedStatus.length}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Contractor Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search contractors..." 
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={(v: string) => setFilterStatus(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expiring-soon">Expiring Soon</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contractor List */}
      <Card>
        <CardHeader>
          <CardTitle>Contractor Directory ({filteredContractors.length} contractors)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoading && <div className="text-sm text-muted-foreground">Loading contractors...</div>}
            {error && <div className="text-sm text-red-500">Failed to load contractors</div>}
            {filteredContractors.map((contractor) => (
              <div key={contractor.id} className="border rounded-lg p-4 hover:bg-muted/25 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg">
                        {contractor.firstName} {contractor.lastName}
                      </h3>
                      {getStatusBadge(contractor)}
                      <Badge variant="outline">{contractor.contractType.replace('-', ' ')}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {contractor.role} • {contractor.company}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedContractor(contractor)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditContractor(contractor)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(contractor.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{contractor.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{contractor.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{contractor.department}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span>${contractor.hourlyRate}/hour</span>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 text-sm mt-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Start: {format(parseISO(contractor.startDate), 'MMM d, yyyy')}</span>
                  </div>
                  {contractor.endDate && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>End: {format(parseISO(contractor.endDate), 'MMM d, yyyy')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span>W-9: {contractor.w9Status}</span>
                  </div>
                  {contractor.endDate && (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                      <span>{(() => {
                        const days = computeDaysUntilExpiry(contractor.endDate);
                        return days !== null ? `${days} days remaining` : '—';
                      })()}</span>
                    </div>
                  )}
                </div>

                {contractor.notes && (
                  <div className="mt-3 p-2 bg-muted rounded text-sm">
                    <span className="font-medium">Notes: </span>
                    {contractor.notes}
                  </div>
                )}
              </div>
            ))}

            {filteredContractors.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="mb-2">No contractors match your current filters.</p>
                <p className="text-sm">Try adjusting your search criteria or add a new contractor.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contractor Details Modal */}
  {selectedContractor && (
        <Dialog open={!!selectedContractor} onOpenChange={() => setSelectedContractor(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {selectedContractor.firstName} {selectedContractor.lastName}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-sm font-medium">Company</Label>
                  <p className="text-sm">{selectedContractor.company}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Role</Label>
                  <p className="text-sm">{selectedContractor.role}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Department</Label>
                  <p className="text-sm">{selectedContractor.department}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Contract Type</Label>
                  <p className="text-sm capitalize">{selectedContractor.contractType.replace('-', ' ')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Email</Label>
                  <p className="text-sm">{selectedContractor.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Phone</Label>
                  <p className="text-sm">{selectedContractor.phone}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Hourly Rate</Label>
                  <p className="text-sm">${selectedContractor.hourlyRate}/hour</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">W-9 Status</Label>
                  <p className="text-sm capitalize">{selectedContractor.w9Status}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Address</Label>
                <p className="text-sm">
                  {selectedContractor.address}<br />
                  {selectedContractor.city}, {selectedContractor.state} {selectedContractor.zipCode}
                </p>
              </div>

              {selectedContractor.notes && (
                <div>
                  <Label className="text-sm font-medium">Notes</Label>
                  <p className="text-sm">{selectedContractor.notes}</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
